import type { ApiErrorBody } from './generated';

export function resolveApiBaseUrl(configured?: string): string {
  const base = (configured?.trim() || '/api/v1').replace(/\/+$/, '');
  return base.endsWith('/api/v1') ? base : `${base}/api/v1`;
}

const baseUrl = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
const csrfStorageKey = 'mento_csrf_header';
const refreshableAuthErrors = new Set(['ACCESS_TOKEN_INVALID', 'AUTH_REQUIRED']);
let refreshPromise: Promise<void> | undefined;

export function apiUrl(path: string): string {
  return `${baseUrl}${path}`;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly requestId?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

function csrfToken(): string | undefined {
  const stored = sessionStorage.getItem(csrfStorageKey);
  if (stored) return stored;
  const part = document.cookie.split('; ').find((cookie) => cookie.startsWith('mento_csrf='));
  return part ? decodeURIComponent(part.slice('mento_csrf='.length)) : undefined;
}

function captureCsrfToken(response: Response): void {
  const token = response.headers.get('x-csrf-token');
  if (token) sessionStorage.setItem(csrfStorageKey, token);
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try { body = await response.json() as ApiErrorBody; } catch { /* Non-JSON gateway response. */ }
  const requestId = body?.error.requestId ?? response.headers.get('x-request-id') ?? undefined;
  return new ApiError(response.status, body?.error.code ?? 'REQUEST_FAILED', body?.error.message ?? 'Сервер вернул ошибку', requestId);
}

function logApiFailure(method: string, url: string, error: ApiError, cause?: unknown): void {
  console.error('[MentoAI API request failed]', {
    method,
    url,
    status: error.status,
    code: error.code,
    requestId: error.requestId,
    ...(cause instanceof Error ? { cause: cause.name } : {}),
  });
}

function requestHeaders(method: string, options: RequestInit): Headers {
  const headers = new Headers(options.headers);
  const csrf = csrfToken();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrf) headers.set('x-csrf-token', csrf);
  return headers;
}

async function refreshAccessToken(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const method = 'POST';
    const url = `${baseUrl}/auth/refresh`;
    let response: Response;
    try {
      response = await fetch(url, { method, credentials: 'include', headers: requestHeaders(method, {}) });
      captureCsrfToken(response);
    } catch (reason) {
      const error = new ApiError(0, 'NETWORK_ERROR', 'Не удалось связаться с сервером');
      logApiFailure(method, url, error, reason);
      throw error;
    }
    if (!response.ok) {
      const error = await parseError(response);
      logApiFailure(method, url, error);
      throw error;
    }
  })().finally(() => { refreshPromise = undefined; });
  return refreshPromise;
}

async function request(url: string, method: string, options: RequestInit, retryAuth = true): Promise<Response> {
  try {
    const response = await fetch(url, { ...options, headers: requestHeaders(method, options) });
    captureCsrfToken(response);
    if (!response.ok) {
      const error = await parseError(response);
      if (retryAuth && !url.endsWith('/auth/refresh') && error.status === 401 && refreshableAuthErrors.has(error.code)) {
        await refreshAccessToken();
        return request(url, method, options, false);
      }
      logApiFailure(method, url, error);
      throw error;
    }
    return response;
  } catch (reason) {
    if (reason instanceof ApiError) throw reason;
    const error = new ApiError(0, 'NETWORK_ERROR', 'Не удалось связаться с сервером');
    logApiFailure(method, url, error, reason);
    throw error;
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = options.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(options.headers);
  if (options.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const url = `${baseUrl}${path}`;
  const response = await request(url, method, { ...options, headers, credentials: 'include' });
  if (response.status === 204) return undefined as T;
  try {
    const result = await response.json() as { data: T };
    return result.data;
  } catch (reason) {
    const error = new ApiError(response.status, 'INVALID_RESPONSE', 'Сервер вернул некорректный ответ', response.headers.get('x-request-id') ?? undefined);
    logApiFailure(method, url, error, reason);
    throw error;
  }
}

export async function apiBlob(path: string): Promise<Blob> {
  const method = 'GET';
  const response = await request(`${baseUrl}${path}`, method, { method, credentials: 'include' });
  return response.blob();
}

interface StreamCallbacks {
  onDelta(delta: string): void;
  onCompleted(data: { id: string; text: string }): void;
  onUsage(data: unknown): void;
  onSafety(data: unknown): void;
  onAccepted?(data: { id: string; text: string; kind: 'voice'; durationMs: number }): void;
  onSessionUpdated?(data: { title?: string; status?: string; expiresAt?: string }): void;
}

async function consumeMessageStream(response: Response, callbacks: StreamCallbacks): Promise<void> {
  if (!response.body) throw new ApiError(502, 'STREAM_UNAVAILABLE', 'Поток ответа недоступен');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      const event = frame.split('\n').find((line) => line.startsWith('event:'))?.slice(6).trim();
      const raw = frame.split('\n').find((line) => line.startsWith('data:'))?.slice(5).trim();
      if (!event || !raw) continue;
      const data = JSON.parse(raw) as Record<string, unknown>;
      if (event === 'message.delta') callbacks.onDelta(String(data.delta ?? ''));
      else if (event === 'message.completed') callbacks.onCompleted(data as unknown as { id: string; text: string });
      else if (event === 'message.accepted') callbacks.onAccepted?.(data as unknown as { id: string; text: string; kind: 'voice'; durationMs: number });
      else if (event === 'usage') callbacks.onUsage(data);
      else if (event === 'safety') callbacks.onSafety(data);
      else if (event === 'session.updated') callbacks.onSessionUpdated?.(data);
      else if (event === 'error') throw new ApiError(Number(data.status ?? 502), String(data.code ?? 'AI_STREAM_ERROR'), String(data.message ?? 'AI временно недоступен'));
    }
  }
}

export async function streamMessage(
  sessionId: string,
  content: string,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${baseUrl}/ai/sessions/${sessionId}/messages`;
  const response = await request(url, 'POST', {
    method: 'POST', credentials: 'include', ...(signal ? { signal } : {}),
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ content }),
  });
  await consumeMessageStream(response, callbacks);
}

export async function streamVoiceMessage(
  sessionId: string,
  audio: Blob,
  durationMs: number,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${baseUrl}/ai/sessions/${sessionId}/voice`;
  const response = await request(url, 'POST', {
    method: 'POST', credentials: 'include', ...(signal ? { signal } : {}),
    headers: { 'content-type': audio.type || 'audio/webm', 'x-voice-duration-ms': String(Math.round(durationMs)) },
    body: audio,
  });
  await consumeMessageStream(response, callbacks);
}
